import { type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useDebounce } from '@gonfalon/async/useDebounce';

import {
  CommandPaletteDerivedQueryContext,
  type CommandPaletteDerivedQueryContextValue,
} from './command-palette-derived-query-context';
import { CommandPaletteStateContext } from './command-palette-state-context';

/**
 * Debounce window for the search query. Short enough to feel
 * immediate, long enough that a fast typist's burst collapses into a
 * single fetch. The pending indicator stays lit for the whole window
 * (see `isSearchSettling`), so this delay is never a dead gap.
 */
const DEBOUNCE_MS = 150;

export interface CommandPaletteDerivedQueryProviderProps {
  children: ReactNode;
}

/**
 * Derives the debounced query (and the `isSearchSettling` bridge)
 * from the live reducer query, ONCE, and publishes both via
 * {@link CommandPaletteDerivedQueryContext}.
 *
 * Mounted just inside the state provider so it can read the live
 * query and so everything below it -- both the consumer scope-hooks
 * in the app tree and the palette overlay -- reads a single shared
 * debounce rather than each spinning up its own timer.
 *
 * Debounce mechanics:
 *
 *   - Built on `@gonfalon/async`'s `useDebounce`, which returns a
 *     lodash `DebouncedFunc` (hence `.flush()` / `.cancel()`).
 *   - Empty query is instant: clearing skips the debounce so consumer
 *     children are wiped immediately.
 *   - Entering / leaving a root flushes the pending value so a scope
 *     entered with text already typed (e.g. the `@flags foo` snap)
 *     fetches right away instead of after the window.
 */
export function CommandPaletteDerivedQueryProvider({ children }: CommandPaletteDerivedQueryProviderProps) {
  // Read the state context directly (rather than via
  // `useInternalCommandPaletteState`) to avoid a circular import with
  // the state provider, which mounts this provider.
  const state = useContext(CommandPaletteStateContext);
  const liveQuery = state !== null && state.mode.type === 'open' ? state.mode.query : '';
  const rootCommandId = state !== null && state.mode.type === 'open' ? state.mode.rootCommandId : null;

  const [debouncedQuery, setDebouncedQuery] = useState(liveQuery);
  const setDebouncedSoon = useDebounce(setDebouncedQuery, DEBOUNCE_MS);

  // Feed the live query into the debounce. Clearing to empty is
  // instant (cancel the pending trailing call and commit '' now) so
  // the "empty query wipes children" contract keeps its immediacy.
  useEffect(() => {
    if (liveQuery.trim() === '') {
      setDebouncedSoon.cancel();
      setDebouncedQuery(liveQuery);
    } else {
      setDebouncedSoon(liveQuery);
    }
  }, [liveQuery, setDebouncedSoon]);

  // Flush on scope transitions. Entering a root via a snap with text
  // (`@flags foo`) sets a non-empty in-mode query; without the flush
  // that first fetch would wait a full debounce window. Flushing
  // commits the pending value in the same tick the root changes.
  useEffect(() => {
    setDebouncedSoon.flush();
  }, [rootCommandId, setDebouncedSoon]);

  const isSearchSettling = liveQuery.trim() !== debouncedQuery.trim();

  const value = useMemo<CommandPaletteDerivedQueryContextValue>(
    () => ({ debouncedQuery, isSearchSettling }),
    [debouncedQuery, isSearchSettling],
  );

  return (
    <CommandPaletteDerivedQueryContext.Provider value={value}>{children}</CommandPaletteDerivedQueryContext.Provider>
  );
}
